import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Base_Url } from '../config/domainConfig';
import './HeroSection.css';
import { TermsModal, PrivacyModal } from './LegalModals';

const HeroSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    city: '',
    state: '',
    country: '',
    address: '',
    occupation: '',
    category: '',
    idea: ''
  });
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Modal states
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Dropdown data states
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [categories, setCategories] = useState([]);

  // Loading states
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Category custom input state
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');

  // Dynamic Success Stories Data
  const [successStories] = useState([
    {
      id: 1,
      title: "From Startup to Success",
      description: "A young entrepreneur received funding and grew their business from 0 to 1M+ revenue in just 2 years.",
      author: "Rahul Sharma",
      company: "TechStart Solutions",
      amount: "₹50,00,000",
      image: null
    },
    {
      id: 2,
      title: "Empowering Rural Women",
      description: "A women's cooperative received funding to start a handicraft business, creating jobs for 50+ women.",
      author: "Priya Patel",
      company: "Rural Craft Collective",
      amount: "₹25,00,000",
      image: null
    },
    {
      id: 3,
      title: "Education for All",
      description: "An educational startup received funding to provide quality education to underprivileged children.",
      author: "Amit Kumar",
      company: "EduForAll Foundation",
      amount: "₹75,00,000",
      image: null
    }
  ]);

  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);

  // API Base URL
  const API_BASE_URL = `${Base_Url}/api`;

  const extractList = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload.data)) return payload.data;
    if (payload.data && Array.isArray(payload.data.rows)) return payload.data.rows;
    if (payload.data && Array.isArray(payload.data.list)) return payload.data.list;
    if (Array.isArray(payload.countries)) return payload.countries;
    if (Array.isArray(payload.states)) return payload.states;
    if (Array.isArray(payload.categories)) return payload.categories;
    if (payload.data && payload.data.data && Array.isArray(payload.data.data)) return payload.data.data;
    return [];
  };

  const extractSuccess = (payload) => {
    if (!payload) return false;
    const s = payload.success;
    if (typeof s === 'boolean') return s;
    if (typeof s === 'string') return s.toLowerCase() === 'true' || s === '1';
    if (typeof s === 'number') return s === 1;
    return false;
  };

  // Fetch countries from API
  const fetchCountries = async () => {
    setLoadingCountries(true);
    try {
      const url = `${API_BASE_URL}/country-master/active/list`;
      console.log('[HeroSection] Fetch countries URL:', url);

      const response = await fetch(url);
      const data = await response.json();
      console.log('[HeroSection] Countries API raw response:', data);

      if (extractSuccess(data)) {
        const list = extractList(data);
        setCountries(Array.isArray(list) ? list : []);
      } else {
        const list = extractList(data);
        if (Array.isArray(list) && list.length > 0) {
          setCountries(list);
        } else {
          console.error('[HeroSection] countries api failed (no extractable list):', data);
          setCountries([]);
        }
      }
    } catch (error) {
      console.error('[HeroSection] Error fetching countries:', error);
      setCountries([]);
    } finally {
      setLoadingCountries(false);
    }
  };

  // Fetch states by country
  const fetchStates = async (countryId) => {
    if (!countryId) {
      setStates([]);
      return;
    }

    setLoadingStates(true);
    try {
      const response = await fetch(`${API_BASE_URL}/state-master/country/${countryId}`);
      const data = await response.json();

      if (extractSuccess(data)) {
        setStates(extractList(data));
      } else {
        console.error('[HeroSection] states api failed:', data);
        setStates([]);
      }
    } catch (error) {
      console.error('[HeroSection] Error fetching states:', error);
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  };

  // Fetch categories from API
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await fetch(`${API_BASE_URL}/business-category-master/active/list`);
      const data = await response.json();

      if (extractSuccess(data)) {
        setCategories(extractList(data));
      } else {
        console.error('[HeroSection] categories api failed:', data);
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Add custom category
  const addCustomCategory = async () => {
    if (!customCategory.trim()) return;

    try {
      const response = await fetch(`${API_BASE_URL}/business-category-master`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          categoryname: customCategory.trim(),
          categorycode: customCategory.trim().toUpperCase().replace(/\s+/g, '_'),
          description: `Custom category added by user: ${customCategory}`,
          status: 'active'
        }),
      });

      const data = await response.json();
      if (data.success) {
        setCategories(prev => [...prev, data.data]);
        setFormData(prev => ({
          ...prev,
          category: data.data.categoryid
        }));
        setCustomCategory('');
        setShowCustomCategory(false);
      }
    } catch (error) {
      console.error('Error adding custom category:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchCountries();
    fetchCategories();
  }, []);

  // Fetch states when country changes
  useEffect(() => {
    if (formData.country) {
      fetchStates(formData.country);
      setFormData(prev => ({
        ...prev,
        state: ''
      }));
    } else {
      setStates([]);
    }
  }, [formData.country]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (type === 'video') {
      setSelectedVideo(file);
    } else if (type === 'pdf') {
      setSelectedPdf(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitting(true);

    try {
      const formDataToSend = new FormData();

      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      if (selectedVideo) {
        formDataToSend.append('video_file', selectedVideo);
      }
      if (selectedPdf) {
        formDataToSend.append('pdf_file', selectedPdf);
      }

      const response = await fetch(`${API_BASE_URL}/applications/submit`, {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to submit application');
      }

      const userToken = localStorage.getItem('userToken');

      // 1) Create PayU payment request from backend
      const paymentResponse = await fetch(`${API_BASE_URL}/payments/create-payu`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(userToken ? { 'Authorization': `Bearer ${userToken}` } : {})
        },
        body: JSON.stringify({
          applicationId: data.data.applicationId
        }),
      });

      const paymentData = await paymentResponse.json();

      if (!paymentData.success) {
        throw new Error(paymentData.message || 'Failed to create PayU payment');
      }

      const { paymentUrl, formFields } = paymentData.data;

      // 2) Auto-submit PayU payment form (hosted payment flow)
      if (!paymentUrl || !formFields) {
        throw new Error('PayU paymentUrl/formFields missing');
      }

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = paymentUrl;

      Object.entries(formFields).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value ?? '';
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();


    } catch (error) {
      console.error('Form submission error:', error);
      alert('Error submitting application: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDotClick = (index) => {
    setCurrentStoryIndex(index);
  };

  const currentStory = successStories[currentStoryIndex];

  return (
    <section id="home" className="hero-section">
      <div className="hero-container">
        {/* Left Column - Success Stories */}
        <div className="hero-left">
          <div className="success-stories">
            <h1 className="success-title">{t('stories.title')}</h1>

            <div className="story-content">
              <h3 className="story-title">{currentStory.title}</h3>
              <p className="story-description">{currentStory.description}</p>
              <div className="story-details">
                <div className="story-author">
                  <strong>{currentStory.author}</strong>
                  <span>{currentStory.company}</span>
                </div>
                <div className="story-amount">
                  <span className="amount-label">{t('periods.funding_received')}</span>
                  <span className="amount-value">{currentStory.amount}</span>
                </div>
              </div>
            </div>

            <div className="pagination-dots">
              {successStories.map((_, index) => (
                <span
                  key={index}
                  className={`dot ${index === currentStoryIndex ? 'active' : ''}`}
                  onClick={() => handleDotClick(index)}
                ></span>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Registration Form */}
        <div className="hero-right">
          <div className="registration-form-container">
            <h2 className="registration-form-title">
              {t('form.submit')}
            </h2>

            <form
              className="registration-form-wrapper"
              onSubmit={handleSubmit}
            >
              {/* Full Name */}
              <div className="registration-row single-field-row">
                <div className="registration-column full-width-column">
                  <div className="registration-group">
                    <label htmlFor="name" className="registration-label">
                      {t('form.full_name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="registration-input"
                      placeholder="Enter Your Full Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="registration-row single-field-row">
                <div className="registration-column full-width-column">
                  <div className="registration-group">
                    <label htmlFor="address" className="registration-label">
                      Address
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      className="registration-input"
                      placeholder="Enter Your Full Address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Email + Phone */}
              <div className="registration-row">
                <div className="registration-column">
                  <div className="registration-group">
                    <label htmlFor="email" className="registration-label">
                      {t('form.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="registration-input"
                      placeholder="Your Email Address"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="registration-column">
                  <div className="registration-group">
                    <label htmlFor="phone" className="registration-label">
                      {t('form.phone')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="registration-input"
                      placeholder="Your Mobile Number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Age + Country */}
              <div className="registration-row">
                <div className="registration-column">
                  <div className="registration-group">
                    <label htmlFor="age" className="registration-label">
                      Enter Your Age
                    </label>
                    <input
                      type="number"
                      id="age"
                      name="age"
                      className="registration-input"
                      placeholder="Your Age"
                      value={formData.age}
                      onChange={handleInputChange}
                      min="1"
                      max="120"
                      required
                    />
                  </div>
                </div>

                <div className="registration-column">
                  <div className="registration-group">
                    <label htmlFor="country" className="registration-label">
                      Country
                    </label>
                    <select
                      id="country"
                      name="country"
                      className="registration-select"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                      disabled={loadingCountries}
                    >
                      <option value="">Select Country</option>
                      {loadingCountries ? (
                        <option value="" disabled>Loading countries...</option>
                      ) : countries.length === 0 ? (
                        <option value="" disabled>No countries available</option>
                      ) : (
                        countries.map(country => (
                          <option key={country.countryid} value={country.countryid}>
                            {country.countryname}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
              </div>


              {/* State + City */}
              <div className="registration-row">
                <div className="registration-column">
                  <div className="registration-group">
                    <label htmlFor="state" className="registration-label">
                      State
                    </label>
                    <select
                      id="state"
                      name="state"
                      className="registration-select"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      disabled={loadingStates || !formData.country}
                    >
                      <option value="">Select State</option>
                      {loadingStates ? (
                        <option value="" disabled>Loading states...</option>
                      ) : !formData.country ? (
                        <option value="" disabled>Select country first</option>
                      ) : states.length === 0 ? (
                        <option value="" disabled>No states available</option>
                      ) : (
                        states.map(state => (
                          <option key={state.stateid} value={state.stateid}>
                            {state.statename}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>

                <div className="registration-column">
                  <div className="registration-group">
                    <label htmlFor="city" className="registration-label">
                      City
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      className="registration-input"
                      placeholder="Your City"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>


              {/* Occupation + Category */}
              <div className="registration-row">
                <div className="registration-column">
                  <div className="registration-group">
                    <label htmlFor="occupation" className="registration-label">
                      Occupation
                    </label>
                    <input
                      type="text"
                      id="occupation"
                      name="occupation"
                      className="registration-input"
                      placeholder="Your Occupation / Designation"
                      value={formData.occupation}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="registration-column">
                  <div className="registration-group">
                    <label htmlFor="category" className="registration-label">
                      {t('form.business_category')}
                    </label>
                    {!showCustomCategory ? (
                      <select
                        id="category"
                        name="category"
                        className="registration-select"
                        value={formData.category}
                        onChange={(e) => {
                          if (e.target.value === 'other') {
                            setShowCustomCategory(true);
                            setFormData(prev => ({
                              ...prev,
                              category: ''
                            }));
                          } else {
                            setFormData(prev => ({
                              ...prev,
                              category: e.target.value
                            }));
                          }
                        }}
                        required
                        disabled={loadingCategories}
                      >
                        <option value="">Select Category</option>
                        {loadingCategories ? (
                          <option value="" disabled>Loading categories...</option>
                        ) : (
                          <>
                            {categories.map(category => (
                              <option key={category.categoryid} value={category.categoryid}>
                                {category.categoryname}
                              </option>
                            ))}
                            <option value="other">Other</option>
                          </>
                        )}
                      </select>
                    ) : (
                      <div className="custom-category-container">
                        <input
                          type="text"
                          id="custom-category"
                          className="registration-input"
                          placeholder="Enter your category"
                          value={customCategory}
                          onChange={(e) => setCustomCategory(e.target.value)}
                        />
                        <div className="custom-category-buttons">
                          <button
                            type="button"
                            className="save-category-btn"
                            onClick={addCustomCategory}
                            disabled={!customCategory.trim()}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="cancel-category-btn"
                            onClick={() => {
                              setShowCustomCategory(false);
                              setCustomCategory('');
                              setFormData(prev => ({
                                ...prev,
                                category: ''
                              }));
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              
              
              {/* Business Idea */}
              <div className="registration-row single-field-row">
                <div className="registration-column full-width-column">
                  <div className="registration-group">
                    <label htmlFor="idea" className="registration-label">
                      Business Idea
                    </label>
                    <textarea
                      id="idea"
                      name="idea"
                      className="registration-textarea"
                      placeholder="Describe your idea..."
                      value={formData.idea}
                      onChange={handleInputChange}
                      rows="5"
                      required
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* File Upload */}
              <div className="registration-row">
                <div className="registration-column">
                  <div className="registration-group">
                    <label htmlFor="video-upload" className="registration-label">
                      Upload Video File
                    </label>
                    <input
                      id="video-upload"
                      type="file"
                      className="registration-file"
                      accept="video/*"
                      onChange={(e) => handleFileChange(e, 'video')}
                    />
                  </div>
                </div>

                <div className="registration-column">
                  <div className="registration-group">
                    <label htmlFor="pdf-upload" className="registration-label">
                      Upload Your PDF Document
                    </label>
                    <input
                      id="pdf-upload"
                      type="file"
                      className="registration-file"
                      accept=".pdf"
                      onChange={(e) => handleFileChange(e, 'pdf')}
                    />
                  </div>
                </div>
              </div>

              {/* Terms Checkbox */}
              <div className="registration-terms-group">
                <label className="registration-checkbox-container">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    required
                  />
                  <span className="registration-checkmark"></span>
                  <span className="registration-terms-text">
                    I have read and understood all the Terms and Conditions. I agree to your{" "}
                    <button
                      type="button"
                      className="registration-link-btn"
                      onClick={() => setShowTermsModal(true)}
                    >
                      Terms & Conditions
                    </button>
                    {" "}and{" "}
                    <button
                      type="button"
                      className="registration-link-btn"
                      onClick={() => setShowPrivacyModal(true)}
                    >
                      Privacy Policy
                    </button>
                    .
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit1"
                className="registration-submit-btn"
                disabled={submitting || !termsAccepted}
              >
                {submitting ? t('common.loading') : t('form.submit1')}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />

      {/* Privacy Policy Modal */}
      <PrivacyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
    </section>
  );
};

export default HeroSection;